/**
 * Read utm_* campaign params off the current URL for first-party attribution.
 *
 * Outbound recovery / trigger-reentry message links carry
 * utm_source/medium/campaign/content (built server-side by the ONE shared helper
 * app/services/messaging/message_attribution.py). Attaching them to the owned
 * landing beacon lets events_raw.params answer "which template/campaign drove
 * this return + payment" first-party (rule 55) — the beacon's params column is a
 * pass-through (deny-list on key shape only), so these keys flow to the warehouse
 * with no allow-list change.
 *
 * Mirrors readReportParams (KundaliResult.helpers.ts): reads the query string AND
 * the hash so a utm survives a hash-routed landing.
 */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_id"] as const;

/** @internal */
export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

export type MessageLandingParams = UtmParams & {
  recovery_order_id?: string;
};

function locationParams(): [URLSearchParams, URLSearchParams] | null {
  if (typeof window === "undefined") return null;
  return [
    new URLSearchParams(window.location.search),
    new URLSearchParams(window.location.hash.replace(/^#/, "")),
  ];
}

function firstParam(params: [URLSearchParams, URLSearchParams], name: string): string | null {
  return params[0].get(name) || params[1].get(name);
}

/** @internal */
export function readUtmParams(): UtmParams {
  const params = locationParams();
  if (!params) return {};
  const out: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = firstParam(params, key);
    // Keep short, sane values only — never bloat params with an oversized string.
    if (value && value.length <= 100) out[key] = value;
  }
  return out;
}

/**
 * Add the abandoned provider order id only on an explicit checkout-recovery
 * landing. The URL already carries this opaque id so the result surface can
 * reopen the same order; copying it into the owned page-view beacon makes the
 * universal landing pulse joinable to recovery sent/paid rows without exposing
 * a capability token or accepting arbitrary order-like query parameters.
 */
export function readMessageLandingParams(): MessageLandingParams {
  const out: MessageLandingParams = readUtmParams();
  const params = locationParams();
  if (!params || firstParam(params, "checkout_recovery") !== "1") return out;
  const orderId = firstParam(params, "order_id") ?? "";
  if (/^order_[A-Za-z0-9_-]{1,94}$/.test(orderId)) {
    out.recovery_order_id = orderId;
  }
  return out;
}
